import React, { useState, useEffect } from 'react';
import { Box, Typography, Tooltip, Drawer } from '@mui/material';
import { toast } from 'react-toastify';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CustomTextField from '../../form/CustomTextField';
import CustomSelectField from '../../form/CustomSelectField';
import CustomDateTimeField from '../../form/CustomDateTimeField';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { createNotificationAction, updateNotificationAction } from '../../redux/actions/notification';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import strings from '../../i18n/language';

const BRAND = '#3b95e8';
const BORDER = 'rgba(255,255,255,0.08)';
const MUTED = 'rgba(232,240,254,0.38)';

const listTypes = [
  { value: 'standard', label: strings.LABEL_NOTIFICATION_STANDARD },
  { value: 'survey', label: strings.LABEL_NOTIFICATION_SURVEY },
];

const FieldRow = ({ tooltip, children }) => (
  <div style={{ marginBottom: 24 }}>
    <div style={{ position: 'relative' }}>
      {children}
      {tooltip && (
        <Tooltip title={tooltip} placement="left">
          <InfoOutlinedIcon style={{ fontSize: 13, color: MUTED, cursor: 'help', position: 'absolute', right: -20, top: '50%', transform: 'translateY(-50%)' }} />
        </Tooltip>
      )}
    </div>
  </div>
);

const NotificationEdit = ({ open, onClose }) => {
  const dispatch = useDispatch();

  const [title, setTitle] = useState(null);
  const [content, setContent] = useState(null);
  const [answer, setAnswer] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [type, setType] = useState('standard');
  const [dateTime, setDateTime] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [valid, setValid] = useState(false);
  const [errorTitle, setErrorTitle] = useState(true);
  const [errorAnswer, setErrorAnswer] = useState(true);
  const [errorContent, setErrorContent] = useState(true);
  const [errorDateTime, setErrorDateTime] = useState(true);
  const [originalNotification, setOriginalNotification] = useState({});

  const notification = useSelector((state) => state.notification.notification);
  const selectedCompany = useSelector((state) => state.login.selectedCompany);

  const isEdit = !!(notification && notification.id);

  useEffect(() => {
    if (!open) return;
    if (notification) {
      setOriginalNotification(notification);
      setTitle(notification.title);
      setType(notification.type);
      if (notification.datetime) setDateTime(dayjs(notification.datetime));
      if (notification.answers) setAnswers(JSON.parse(notification.answers));
      setContent(notification.content);
    } else {
      setOriginalNotification({});
      setTitle(null);
      setContent(null);
      setAnswer(null);
      setAnswers([]);
      setType('standard');
      setDateTime(null);
      setDirty(false);
    }
  }, [open, notification]);

  useEffect(() => {
    const re = /^[a-z0-9\sáéíóúñüäöüß.,;:!?'"\-()%]+$/i;
    setErrorTitle(!(title && title.length > 4 && title.length < 60 && re.test(title)));
  }, [title]);

  useEffect(() => {
    const re = /^[a-z0-9\sáéíóúñüäöüß.,;:!?'"\-()%]+$/i;
    setErrorContent(!(content && content.trim().length > 10 && content.trim().length < 100 && re.test(content)));
  }, [content]);

  useEffect(() => {
    const oneHourFromNow = dayjs().add(1, 'hour');
    setErrorDateTime(!dateTime || !dayjs(dateTime).isValid() || dayjs(dateTime).isBefore(oneHourFromNow));
  }, [dateTime]);

  useEffect(() => {
    setDirty(originalNotification && (
      type !== originalNotification.type ||
      title !== originalNotification.title ||
      content !== originalNotification.content ||
      JSON.stringify(answers) !== originalNotification.answers
    ));
  }, [type, title, content, answers, originalNotification]);

  useEffect(() => {
    setValid(!errorTitle && !errorContent && !errorDateTime && !(type === 'survey' && errorAnswer));
  }, [errorTitle, errorContent, errorDateTime, errorAnswer, type]);

  useEffect(() => {
    setErrorAnswer(answers.length < 2 || answers.length > 5);
  }, [answers]);

  const handleKeyDown = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    const re = /^[a-z0-9\s]+$/i;
    if (answer && answer.length > 4 && answer.length < 21 && re.test(answer)) {
      setAnswers([...answers, { id: answers.length ? answers[answers.length - 1].id + 1 : 0, label: answer }]);
      setAnswer(null);
    }
  };

  const save = () => {
    if (errorTitle) { toast.error(strings.LABEL_NOTIFICATION_TITLE_ERROR); return; }
    if (errorContent) { toast.error(strings.LABEL_NOTIFICATION_CONTENT_ERROR); return; }
    if (errorDateTime) { toast.error(strings.LABEL_NOTIFICATION_DATE_ERROR); return; }
    if (type === 'survey' && errorAnswer) { toast.error(strings.LABEL_NOTIFICATION_ANSWER_ERROR); return; }
    const answersStr = type === 'standard' ? JSON.stringify([]) : JSON.stringify(answers);
    const status = 'planned';
    if (isEdit) {
      dispatch(updateNotificationAction({ id: notification.id, status, content, title, datetime: dateTime, type, answers: answersStr }));
    } else {
      dispatch(createNotificationAction({ status, content, title, datetime: dateTime, type, answers: answersStr, company_id: selectedCompany?.id }));
    }
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 520,
          background: '#0d1b36',
          borderLeft: `1px solid ${BORDER}`,
          boxShadow: '-24px 0 60px rgba(0,0,0,0.4)',
        },
      }}
    >
      <ToastContainer position="bottom-right" />

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2, borderBottom: `1px solid ${BORDER}`, flexShrink: 0 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#e8f0fe' }}>
          {isEdit ? 'Editar notificación' : 'Nueva notificación'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <button onClick={onClose} style={{
            padding: '7px 14px', borderRadius: 8, fontSize: 13, fontWeight: 500,
            background: 'rgba(255,255,255,0.05)', color: MUTED,
            border: `1px solid ${BORDER}`, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 5,
          }}>
            <CloseRoundedIcon style={{ fontSize: 15 }} /> Cancelar
          </button>
          <button onClick={save} disabled={!dirty} style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '7px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
            background: dirty ? BRAND : 'rgba(59,149,232,0.15)',
            color: dirty ? '#fff' : 'rgba(59,149,232,0.4)',
            border: 'none', cursor: dirty ? 'pointer' : 'default',
            boxShadow: dirty ? '0 4px 14px rgba(59,149,232,0.3)' : 'none',
            transition: 'all 0.2s',
          }}>
            <SaveRoundedIcon style={{ fontSize: 15 }} /> Guardar
          </button>
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ p: 3, overflowY: 'auto' }}>
        <FieldRow tooltip={strings.LABEL_NOTIFICATION_TOOLTIP_TYPE}>
          <CustomSelectField onChange={setType} title={strings.LABEL_NOTIFICATION_TYPE} id="type" value={type} defaultValues={listTypes} margin="none" />
        </FieldRow>

        <FieldRow>
          <CustomTextField id="title" label={strings.LABEL_NOTIFICATION_TITLE} value={title} onChange={setTitle} margin="none" autoFocus />
        </FieldRow>

        <FieldRow tooltip={strings.LABEL_NOTIFICATION_TOOLTIP_DATE}>
          <CustomDateTimeField value={dateTime} onChange={setDateTime} isValid={errorDateTime} label={strings.LABEL_NOTIFICATION_DATE} minDateTime={dayjs().add(1, 'hour')} />
        </FieldRow>

        <FieldRow>
          <CustomTextField id="content" label={strings.LABEL_NOTIFICATION_CONTENT} value={content} onChange={setContent} margin="none" autoComplete="off" />
        </FieldRow>

        {type === 'survey' && (
          <FieldRow tooltip={strings.LABEL_NOTIFICATION_TOOLTIP_ANSWER}>
            <CustomTextField
              id="answer" label="Añadir respuesta (Enter)" value={answer}
              onChange={setAnswer} margin="none" autoComplete="off" handleKeyDown={handleKeyDown}
            />
            {answers.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
                {answers.map((a) => (
                  <div key={a.id} style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    background: 'rgba(139,159,212,0.13)', border: '1px solid rgba(139,159,212,0.25)',
                    borderRadius: 8, padding: '5px 10px',
                    fontSize: 12, color: '#8b9fd4',
                  }}>
                    {a.label}
                    <button onClick={() => setAnswers(answers.filter((x) => x.id !== a.id))} style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: 'rgba(139,159,212,0.6)', padding: 0,
                      display: 'flex', alignItems: 'center',
                    }}>
                      <CloseRoundedIcon style={{ fontSize: 13 }} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </FieldRow>
        )}
      </Box>
    </Drawer>
  );
};

export default NotificationEdit;
